import React from 'react';
import { useCombobox } from 'downshift';
import { getBrowserTz, DEFAULT_TIMEZONE } from '../lib/timezone';
import { buildZoneOptions, groupZones, readRecents, pushRecent } from '../lib/timezoneOptions';
import { palette as T, alpha, type } from '../design';

// Searchable, themed timezone picker (TTT-67). Replaces a native <select> of all 419 IANA
// zones — unscannable, and its dropdown rendered in OS chrome that ignores the theme.
//
// Built on Downshift's useCombobox rather than hand-rolled: aria-activedescendant, focus
// return and typeahead announcements are the parts that break silently.
//
// Groups: Detected → Recently used → All timezones, each row labeled with its live offset.
// Nothing is merged by offset (see timezoneOptions.js).

const font = "'Plus Jakarta Sans', sans-serif";

// Sizing lives on the wrapper so it occupies the same box the <select> did (callers pass
// width:100% on profile forms, maxWidth:60% on the inline "times shown in" toggles).
// Everything else — border, padding, background — is the input's.
const BOX_KEYS = ['width', 'maxWidth', 'minWidth', 'flex'];

function splitStyle(style = {}) {
  const box = {};
  const inner = {};
  for (const [k, v] of Object.entries(style)) (BOX_KEYS.includes(k) ? box : inner)[k] = v;
  return [box, inner];
}

export function TimezoneSelect({ value, onChange, style, ariaLabel = 'Timezone' }) {
  const options = React.useMemo(() => buildZoneOptions(), []);
  const knownZones = React.useMemo(() => new Set(options.map((o) => o.tz)), [options]);

  const [recents, setRecents] = React.useState(() => readRecents(knownZones));
  const [open, setOpen] = React.useState(false);

  const detected = getBrowserTz() || DEFAULT_TIMEZONE;
  const selectedOption = React.useMemo(
    () => options.find((o) => o.tz === value) || null,
    [options, value]
  );

  // The field holds the SEARCH QUERY and nothing else; the selected zone shows through as the
  // placeholder. Earlier versions put the zone label in the value and cleared it on open —
  // every variant raced the keystroke that triggered the open ("tok" searched for "ok", then
  // for "H(GMT+08:00) Asia/Taipeiou"). With no label in the value there is nothing to restore,
  // so there is no race. The placeholder is styled solid, since it is the current value.
  const [query, setQuery] = React.useState('');

  const { items, headers } = React.useMemo(
    () => groupZones({ options, detected, recents, selected: value, query }),
    [options, detected, recents, value, query]
  );

  const {
    isOpen, getInputProps, getMenuProps, getItemProps, getToggleButtonProps, highlightedIndex,
  } = useCombobox({
    items,
    isOpen: open,
    // inputValue is deliberately NOT controlled. Controlling it made React's async state the
    // source of truth for the DOM, and fast typing outran it — characters were reverted
    // mid-word ("Honolulu" arrived as "Honuu"). Downshift owns the field; we only mirror it
    // into `query` to filter with, which may lag a render but can never lose a keystroke.
    selectedItem: selectedOption,
    itemToString: () => '', // the field is the query, never the selection
    onIsOpenChange: ({ isOpen: next }) => setOpen(next),
    onInputValueChange: ({ inputValue: next }) => setQuery(next ?? ''),
    // Dropping the search once it is finished with — on Escape, on blur, and after a pick —
    // belongs in Downshift's own state, not a controlled prop: the reducer runs synchronously
    // inside its update, so it cannot race the keystrokes the way the controlled value did.
    stateReducer: (state, { changes, type }) => {
      const t = useCombobox.stateChangeTypes;
      const done = type === t.InputBlur || type === t.InputKeyDownEscape
        || type === t.ItemClick || type === t.InputKeyDownEnter;
      return done ? { ...changes, inputValue: '' } : changes;
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;
      pushRecent(selectedItem.tz);
      setRecents(readRecents(knownZones));
      onChange(selectedItem.tz);
    },
  });

  const [boxStyle, inputStyle] = splitStyle(style);

  return (
    <div style={{ position: 'relative', ...boxStyle }}>
      <input
        {...getInputProps({
          'aria-label': ariaLabel,
          className: 'tz-combobox-input',
          placeholder: selectedOption?.label ?? 'Search timezones',
          style: {
            fontFamily: font, fontSize: type.body, color: T.espresso, background: T.white,
            padding: '8px 10px', borderRadius: 10, border: '1px solid ' + T.border,
            outline: 'none', width: '100%', boxSizing: 'border-box', ...inputStyle,
            // After the caller's style: a caller passing shorthand `padding` would otherwise
            // reset the right gap and the zone name would run under the chevron.
            paddingRight: 26,
          },
        })}
      />
      <button
        {...getToggleButtonProps({
          'aria-label': 'Toggle timezone list',
          tabIndex: -1,
          style: {
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: T.driftwood, fontSize: type.caption, lineHeight: 1,
          },
        })}
      >
        ▾
      </button>

      <ul
        {...getMenuProps({
          style: {
            display: isOpen ? 'block' : 'none',
            position: 'absolute', zIndex: 40, top: 'calc(100% + 4px)', left: 0,
            minWidth: '100%', maxHeight: 320, overflowY: 'auto',
            margin: 0, padding: '4px 0', listStyle: 'none',
            background: T.white, border: '1px solid ' + T.border, borderRadius: 12,
            boxShadow: '0 12px 32px ' + alpha('espresso', 'shadow'),
          },
        })}
      >
        {isOpen && items.map((item, index) => (
          // Keyed by index, not by tz: a zone deliberately appears more than once (pinned as
          // Detected or Recently used, and again under its region), so tz is not unique here.
          <React.Fragment key={index}>
            {headers[index] && (
              <li role="presentation" style={{
                fontFamily: font, fontSize: type.caption, fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: T.driftwood,
                padding: '8px 12px 4px', background: T.ivory,
              }}>
                {headers[index]}
              </li>
            )}
            <li
              {...getItemProps({ item, index })}
              style={{
                fontFamily: font, fontSize: type.body, padding: '7px 12px', cursor: 'pointer',
                display: 'flex', gap: 8, alignItems: 'baseline', whiteSpace: 'nowrap',
                background: highlightedIndex === index ? T.warmGrey : 'transparent',
                color: item.tz === value ? T.terracotta : T.espresso,
                fontWeight: item.tz === value ? 600 : 400,
                // ponytail: native off-screen skipping for the 419-row unfiltered list.
                // Swap in virtualization only if an unfiltered open ever measures janky.
                contentVisibility: 'auto', containIntrinsicSize: '0 32px',
              }}
            >
              <span style={{ color: T.driftwood, fontVariantNumeric: 'tabular-nums' }}>
                ({item.offsetLabel})
              </span>
              <span>{item.name}</span>
            </li>
          </React.Fragment>
        ))}
        {isOpen && !items.length && (
          <li role="presentation" style={{
            fontFamily: font, fontSize: type.body, color: T.walnut, padding: '10px 12px',
          }}>
            No timezones match “{query}”
          </li>
        )}
      </ul>
    </div>
  );
}
